import { useEffect, useMemo, useState } from 'react';
import { useRefreshWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { AppExperiments, useIsTreatment } from '../../../utils/experiments';
import { getPendingRoles } from '../utils/gracePeriod';
import type { WorkspaceTeamMember } from '../utils/workspaceTeam';

// setTimeout overflows past 2^31-1 ms (~24.8 days); a longer wait re-arms.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * IDFE-4022 (t0018_4fba688a): the `now` to pass to canManageMembers /
 * canManageEntity so an admin role still in its grace period doesn't grant
 * manage controls. Undefined in control, which keeps today's behavior.
 *
 * In treatment it advances when the current user's earliest pending role takes
 * effect and refetches the members then, so an open page unlocks without a
 * reload.
 */
export function useManagePermissionsNow(
  currentUser: Pick<WorkspaceTeamMember, 'pendingRoles'> | undefined,
  organizationId: string,
): Date | undefined {
  const isTreatment = useIsTreatment(AppExperiments.t0018_4fba688a);
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  const [now, setNow] = useState(() => new Date());

  const nextEffectiveAt = useMemo(
    () =>
      isTreatment && currentUser != null
        ? getPendingRoles(currentUser, now)[0]?.effectiveAt
        : undefined,
    [currentUser, isTreatment, now],
  );

  useEffect(() => {
    if (nextEffectiveAt == null) return undefined;

    const delayMs = Math.min(Math.max(nextEffectiveAt.getTime() - Date.now(), 0), MAX_TIMEOUT_MS);
    const timeoutId = setTimeout(() => {
      setNow(new Date());
      void refreshWorkspaceMembers();
    }, delayMs);

    return () => clearTimeout(timeoutId);
  }, [nextEffectiveAt, refreshWorkspaceMembers]);

  return isTreatment ? now : undefined;
}
